/**
 * Persists Comment RPA modal selections and optional write-back reports.
 * @module commentRpaModalSave
 */

import { AllCommentsProps } from "../../data-model/busniessOfficeNote";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { throwApiError } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/** Request body for the Comment RPA modal save endpoint. */
interface CommentRpaModalSavePayload {
  order_id: string;
  org_id?: string | null;
  bo_status?: string | null;
  overwrite_rpa_errors?: boolean;
  write_back_reports?: AllCommentsProps[] | null;
  auth_status?: string;
  estimate_value?: string;
}

/**
 * POSTs Comment RPA modal data to the backend (BO status, write-back reports, etc.).
 *
 * Failures follow the `/prior-auth-utilities/*` HTTP-status contract: 4xx/5xx
 * responses throw an `Error` enriched with `status` and `detail` (read by
 * `utils/showApiError` on the consumer side).
 *
 * @param payload - Order identifiers, optional BO/auth fields, and reports to persist.
 * @returns Parsed JSON response from the server.
 * @throws {Error & { status: number; detail?: string }} On HTTP failure.
 * @throws {Error} When the bearer token is missing, or on network / parse failure.
 */
export const commentRpaModalSave = async (
  payload: CommentRpaModalSavePayload,
) => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.COMMENT_RPA_MODAL_SAVE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      await throwApiError(response);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
};
